import { useState } from 'react';
import { isObject } from 'underscore';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import UploadLarge from '@ui/uploads/upload-large';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import type { MediaWidgetSize } from '../../../templates/widgets/widget-types';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import SizeControl from '../controls/size-control';
import AlignmentControl from '../controls/alignment-control';
import BorderControl from '../controls/border-control';
import BorderRadiusControl from '../controls/border-radius-control';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedBlockStyle } from '../controls/use-resolved-block-style';

interface LogoStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateSrc: (src: string) => void;
}

export default function LogoStylePanel({ block, onUpdateStyle, onUpdateSrc }: LogoStylePanelProps) {
  const style = block.style ?? {};
  const resolved = useResolvedBlockStyle(block.id, block.style);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | FileList) => {
    if (!(file instanceof File)) return;
    setUploading(true);
    try {
      const endpoint = await GetUploadEndpoint({ filename: getUploadFilename(file.name) });
      if (isObject(endpoint) && endpoint.url) {
        const finalUrl = await UploadFileToProvider(
          { url: endpoint.url, fields: endpoint.fields, cdnHost: endpoint?.cdnHost },
          file
        );
        SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);
        if (finalUrl) onUpdateSrc(finalUrl);
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const src = block.props.src as string | undefined;
  const align = (style.align as string | undefined) ?? 'center';
  const size = (style.size as MediaWidgetSize | undefined) ?? 'original';
  const scale = (style.scale as number | undefined) ?? 100;

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <Container width="308px" height="172px">
        <UploadLarge
          displayImageUrl={src}
          uploading={uploading}
          onFile={handleFile}
          hidePlaceholderImage
          style={{
            width: '308px',
            height: '172px',
            padding: '0 24px',
            borderRadius: '16px',
            border: '1px dashed var(--global-ico-text-3)',
            backgroundColor: 'transparent',
            backgroundOrigin: 'content-box',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png']}
          maxSizeInBytes={1024 * 1024}
          CustomTitle={
            <Container className="flex-column ai-center gap-4">
              <Container className="flex ai-center gap-6">
                <Icon name={ICONS.UPLOAD} size={12} color="var(--global-ico-text-1)" />
                <Text className="S2" color="var(--global-ico-text-1)">
                  Upload logo
                </Text>
              </Container>
              <Text className="C2" color="var(--global-ico-text-3)">
                or drag and drop here
              </Text>
              <Text className="C2" color="var(--global-ico-text-3)">
                1mb | png, jpg, jpeg
              </Text>
            </Container>
          }
        />
      </Container>

      <SizeControl
        size={size}
        scale={scale}
        onChangeSize={(value) => onUpdateStyle('size', value)}
        onChangeScale={(v) => onUpdateStyle('scale', v)}
      />

      <Container className="flex gap-12">
        <ColorPill
          label="Background color"
          value={style.background as string | undefined}
          isOverridden={style.background !== undefined}
          onChange={(v) => onUpdateStyle('background', v)}
          onReset={() => onUpdateStyle('background', undefined)}
        />
        <AlignmentControl value={align} onChange={(value) => onUpdateStyle('align', value)} />
      </Container>

      <BorderControl
        style={style}
        onUpdateStyle={onUpdateStyle}
        resolvedColor={resolved.borderColor}
        resolvedWidth={resolved.borderWidth}
      />

      <BorderRadiusControl
        label="Corner radius"
        blockId={block.id}
        style={style}
        onUpdateStyle={onUpdateStyle}
        expandable
        resolvedValues={[
          resolved.cornerTopLeft,
          resolved.cornerTopRight,
          resolved.cornerBottomRight,
          resolved.cornerBottomLeft,
        ]}
      />

      <BoxModelControl
        sectionLabel="Padding"
        expanded={padding.expanded}
        onToggleExpand={() => padding.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.PADDING_SIDES}
        toggleLabel="padding controls"
        values={padding.values}
        onChangeSlot={padding.setSlot}
        fields={[
          { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
          { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
          { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
          { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Padding"
              icon={ICONS.PADDING_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[0]}
              onChange={padding.setCollapsedFirst}
            />
            <NumberField
              icon={ICONS.PADDING_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[1]}
              onChange={padding.setCollapsedSecond}
            />
          </Container>
        )}
      />
    </Container>
  );
}
